import { useState } from 'react';

import { fetchPostDeReferralsValidate } from '@iwoca/lapi-client/edge_de';
import { Input } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';

import styles from './MobileSignupReferralCode.module.css';
import {
  getReferralSession,
  removeReferralSession,
  saveReferralSession,
} from '../../../../../helpers/referralSession';
import ButtonLink from '../../../../ButtonLink/ButtonLink';

type TValidationStatus = 'idle' | 'confirmed' | 'invalid';

// Mobile port of the desktop ReferralCodeFieldDE. The code isn't part of the
// signup form store — it's validated and saved to the referral session, which
// the account-setup mint (useMobileSignupAccountSetup) already reads. So this
// component owns its own field state and never touches TMobileSignupForm.
type TProps = {
  onValidatingChange: (isValidating: boolean) => void;
};

export const MobileSignupReferralCode = ({ onValidatingChange }: TProps) => {
  const { t } = useLingui();
  const [showField, setShowField] = useState(false);
  const [code, setCode] = useState('');
  const [validationStatus, setValidationStatus] =
    useState<TValidationStatus>('idle');
  const [hasStoredSession] = useState(() => Boolean(getReferralSession()));

  const onBlur = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    try {
      onValidatingChange(true);
      const result = await fetchPostDeReferralsValidate({
        body: { referral_code: trimmed, source_type: 'code' },
      });
      if (result.valid) {
        saveReferralSession(trimmed, result);
        setValidationStatus('confirmed');
      } else {
        setValidationStatus('invalid');
      }
    } catch {
      // Silently ignore — a referral must never block signup.
    } finally {
      onValidatingChange(false);
    }
  };

  if (hasStoredSession) return null;

  return (
    <div className={styles.referralCodeWrapper}>
      {showField ? (
        <Input
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
            setValidationStatus('idle');
            removeReferralSession();
          }}
          onBlur={onBlur}
          label={t`Referral code`}
          placeholder={t`Enter your referral code`}
          data-testid="referral-code-input"
          success={
            validationStatus === 'confirmed'
              ? t`Referral code confirmed`
              : undefined
          }
          error={
            validationStatus === 'invalid'
              ? t`This referral code is not valid`
              : undefined
          }
        />
      ) : (
        <ButtonLink
          className={styles.toggle}
          onClick={() => setShowField(true)}
          data-testid="referral-code-toggle"
        >
          <Trans>I have a referral code</Trans>
        </ButtonLink>
      )}
    </div>
  );
};
